(() => {
  const API_NAME = 'NordwegProductCardRuntimeLoader';
  const CONFIG_SELECTOR = 'script[data-product-card-runtime-loader]';
  const TARGET_SELECTOR = 'product-recommendations, homepage-collection-product-card, collection-product-card';
  const INTENT_EVENTS = ['pointerdown', 'touchstart', 'focusin'];
  const RUNTIME_PATH = /^\/cdn\/shop\/t\/\d+\/assets\/homepage-product-card-embla\.js$/;

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const preparedTargets = new Set();
  const observedTargets = new WeakSet();
  let observer = null;
  let runtimeUrl = '';
  let runtimePromise = null;
  let loaded = false;

  function configs() {
    return [...document.querySelectorAll(CONFIG_SELECTOR)];
  }

  function isLoaded() {
    return loaded;
  }

  function trustedRuntimeUrl(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' || url.origin !== window.location.origin) return '';
      if (!RUNTIME_PATH.test(url.pathname)) return '';
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  function cleanupTargets() {
    preparedTargets.forEach((target) => {
      INTENT_EVENTS.forEach((eventName) => target.removeEventListener(eventName, handleIntent));
    });
    preparedTargets.clear();
  }

  function load() {
    if (loaded) return Promise.resolve();
    if (runtimePromise) return runtimePromise;

    const currentConfigs = configs();
    const currentConfig = currentConfigs[currentConfigs.length - 1];
    runtimeUrl = trustedRuntimeUrl(currentConfig?.dataset.productCardRuntimeScript) || runtimeUrl;
    if (!runtimeUrl) return Promise.reject(new Error('Product-card runtime URL is unavailable.'));

    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = runtimeUrl;
      script.async = true;
      script.dataset.productCardRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'error') {
          script.remove();
          reject(new Error(`Failed to load product-card runtime: ${runtimeUrl}`));
          return;
        }
        loaded = true;
        resolve();
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).then(() => {
      observer?.disconnect();
      cleanupTargets();
    }).catch((error) => {
      runtimePromise = null;
      throw error;
    });

    return runtimePromise;
  }

  function handleIntent() {
    load().catch(() => false);
  }

  function prepareTarget(target) {
    if (!target || preparedTargets.has(target) || loaded) return;
    preparedTargets.add(target);
    INTENT_EVENTS.forEach((eventName) => {
      target.addEventListener(eventName, handleIntent, { passive: true });
    });
  }

  function observeTarget(target) {
    if (!target || observedTargets.has(target) || loaded) return;
    prepareTarget(target);
    observedTargets.add(target);
    observer.observe(target);
  }

  function targetsIn(root = document) {
    const targets = [];
    if (root.matches?.(TARGET_SELECTOR)) targets.push(root);
    root.querySelectorAll?.(TARGET_SELECTOR).forEach((target) => targets.push(target));
    return targets;
  }

  function refresh(root = document) {
    const currentConfigs = configs();
    const currentConfig = currentConfigs[currentConfigs.length - 1];
    runtimeUrl = trustedRuntimeUrl(currentConfig?.dataset.productCardRuntimeScript) || runtimeUrl;
    if (loaded || currentConfigs.length === 0) return;

    if (currentConfigs.some((config) => config.dataset.designMode === 'true')) {
      load().catch(() => false);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      load().catch(() => false);
      return;
    }

    const currentTargets = targetsIn(root);
    if (navigator.connection?.saveData) {
      currentTargets.forEach(prepareTarget);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load().catch(() => false);
      }, { rootMargin: '1600px 0px', threshold: 0 });
    }

    currentTargets.forEach(observeTarget);
  }

  const api = { isLoaded, load, refresh };
  window[API_NAME] = api;

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  refresh();
})();
